import { DateUtility } from '@/utils/date.utils';
import { Placement } from '@floating-ui/react';
import { useCallback, useState } from 'react';
import { DateRange, DayPicker, getDefaultClassNames } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button';
import { Dropdown, TimeTag } from './components';
import { CUSTOM_DAYS_SELECT, TagType } from './constants';
import { useDatesPlaceholder } from './hooks';

type DateTag = {
  type: TagType;
  value: number | undefined;
};

type DateRangeDropdownProps = {
  label?: string;
  range?: DateRange;
  className?: string;
  onChange?: (range?: DateRange) => void;
  isDisabled?: boolean;
  placement?: Placement;
  containerClassName?: string;
  onRemoveAllSelectedClick?: () => void;
};

const getTagFromDate = (
  type: TagType,
  days?: number,
): DateRange | undefined => {
  const currentDate = DateUtility.today();

  switch (type) {
    case TagType.ALL_TIME:
      return;
    case TagType.YEAR_TO_DATE:
      return { from: DateUtility.firstDayOfYear(), to: currentDate };
    case TagType.LAST_DAYS:
    default:
      return {
        from: DateUtility.subtract(currentDate, {
          days: days || 7,
        }),
        to: currentDate,
      };
  }
};

export const DateRangeDropdown = ({
  label,
  range,
  className,
  onChange,
  isDisabled,
  containerClassName,
  placement,
  onRemoveAllSelectedClick,
}: DateRangeDropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<DateTag>();
  const [appliedTag, setAppliedTag] = useState<DateTag>();
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    range,
  );
  const datesPlaceholder = useDatesPlaceholder(
    selectedRange,
    selectedTag?.type,
  );
  const weeklyDays = t('date_picker.weekly_days', {
    returnObjects: true,
  }) as string[];

  const monthNames = t('date_picker.month_names', {
    returnObjects: true,
  }) as string[];

  const placeholder = t(datesPlaceholder.label, {
    ...datesPlaceholder.opt,
    interpolation: { escapeValue: false },
  });

  const handleApply = useCallback(() => {
    setIsOpen(false);
    onChange?.(selectedRange);
    setAppliedTag(selectedTag);
  }, [onChange, selectedRange, selectedTag]);

  const handleTagsSelect = useCallback(
    (type: TagType, days?: number) => {
      const newDateRange = getTagFromDate(type, days);
      const dateTag = { type, value: days };
      setSelectedTag(dateTag);
      setSelectedRange(newDateRange);
      setAppliedTag(dateTag);
      onChange?.(newDateRange);
      setIsOpen(false);
    },
    [onChange],
  );

  const handleDateChange = useCallback((dateRange: DateRange | undefined) => {
    setSelectedTag(undefined);

    if (dateRange?.to) {
      const adjustedTo = new Date(dateRange.to);
      adjustedTo.setHours(23, 59, 59, 999);
      dateRange.to = adjustedTo;
    }

    if (dateRange?.from) {
      const adjustedFrom = new Date(dateRange.from);
      adjustedFrom.setHours(0, 0, 0, 0);
      dateRange.from = adjustedFrom;
    }

    setSelectedRange({
      from: dateRange?.from,
      to: dateRange?.to,
    });
  }, []);

  const onCancelClick = useCallback(() => {
    setSelectedRange(range);
    setSelectedTag(appliedTag);
    setIsOpen(false);
  }, [appliedTag, range]);

  const onRemoveFilter = useCallback(() => {
    setSelectedRange(undefined);
    setSelectedTag(undefined);
    setAppliedTag(undefined);
    onRemoveAllSelectedClick?.();
  }, [onRemoveAllSelectedClick]);

  return (
    <div
      className={twMerge(
        'sh flex h-full w-full items-center space-x-2 text-nowrap',
        containerClassName,
      )}
    >
      {!!label && (
        <div className="border-white text-md font-semibold">{label}</div>
      )}
      <Dropdown
        isOpen={isOpen}
        placeholder={placeholder}
        hiddenClassName="border-t p-2 rounded-md z-[60]"
        buttonClassMame={twMerge('rounded-md')}
        containerClassName={className}
        placement={placement}
        setIsOpen={setIsOpen}
        disabled={isDisabled}
        onOutsideClick={onCancelClick}
        onRemoveAllSelectedClick={onRemoveFilter}
      >
        <div className="z-60 flex flex-col gap-5 p-1">
          <div className="grid grid-cols-2 gap-[5px]">
            {CUSTOM_DAYS_SELECT.map((dateSelection) => (
              <TimeTag
                onClick={handleTagsSelect}
                key={`custom-days-${dateSelection.type}-${dateSelection.value}`}
                type={dateSelection.type}
                value={dateSelection.value}
                selected={
                  dateSelection.type === selectedTag?.type &&
                  dateSelection.value === selectedTag.value
                }
              />
            ))}
          </div>
          <DayPicker
            classNames={{
              button_previous: 'size-5',
              button_next: 'size-5',
              chevron: 'size-4 hover:fill-blue-500 fill-[#1A2A56]',
              caption_label: 'text-sm font-[900] self-center',
              day: `text-sm font-black text-[#1A2A56] border border-transparent data-[today=true]:border-blue-500 data-[outside=true]:text-gray-200`,
              month_caption: 'flex !h-5 text-[#1A2A56]',
              nav: `${getDefaultClassNames().nav} !h-5`,
              selected: 'font-black',
              weeks: `${getDefaultClassNames().weeks} text-sm`,
              month_grid: `${getDefaultClassNames().month_grid} flex flex-col gap-[7px]`,
              today: 'text-gray',
              weekday: 'text-sm font-normal text-gray-300',
              month: 'gap-[5px] flex flex-col !w-full',
              months: '!w-full',
            }}
            modifiersClassNames={{
              range_middle: 'bg-blue-100',
              range_start: 'bg-blue-500 !text-white',
              range_end: 'bg-blue-500 !text-white',
            }}
            defaultMonth={selectedRange?.to ?? new Date()}
            mode="range"
            selected={selectedRange}
            onSelect={handleDateChange}
            ISOWeek
            showOutsideDays
            formatters={{
              formatCaption(date) {
                return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
              },
              formatWeekdayName: (weekday) => {
                return weeklyDays[weekday.getDay()];
              },
            }}
            styles={{
              weekdays: {
                width: 'inherit',
                gap: '7px',
                display: 'flex',
                justifyContent: 'space-between',
              },
              week: {
                width: 'inherit',
                gap: '7px',
                display: 'flex',
                justifyContent: 'space-between',
              },
              weeks: {
                width: 'inherit',
                height: 'min-content',
                display: 'flex',
                flexDirection: 'column',
                gap: '7px',
              },
              day: {
                display: 'flex',
                width: '20px',
                height: '20px',
                padding: '2px',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '3px',
              },
              day_button: { width: '14px', height: '14px' },
            }}
          />
          <div className="flex flex-1 justify-end gap-2">
            <Button
              className="flex-1 font-[900] capitalize"
              onClick={onCancelClick}
              variant="outline"
            >
              {t('cancel')}
            </Button>
            <Button
              className="flex-1 font-[900] capitalize"
              onClick={handleApply}
              variant="primary"
            >
              {t('apply')}
            </Button>
          </div>
        </div>
      </Dropdown>
    </div>
  );
};
